import styled from 'styled-components';
import React, { Fragment } from 'react';
import { Text } from '@lmn/design-system';
import type { TextProps, TextVariant } from '@lmn/design-system';
import { Icon } from '@/app/common/icons/Icon';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import TranslateSafeText from '@/app/common/components/TranslateSafeText';

type RoomMainAmenitiesProps = {
  amenities?: {
    type: string;
    description: string;
    highlight?: boolean;
  }[];
  color?: TextProps['color'];
  textVariant?: TextVariant;
  iconColor: 'iconSecondary' | 'iconPrimary';
  iconSize: number;
} & React.HTMLAttributes<HTMLElement>;

type RoomMainAmenitiesInlineProps = {
  amenities?: {
    type: string;
    description: string;
    highlight?: boolean;
  }[];
} & React.HTMLAttributes<HTMLElement>;

type AmenityIcon = StarfishIconRef;

const BATHTUB: AmenityIcon = { category: 'Services', name: 'icon-bathtub' };
const SOUNDPROOF: AmenityIcon = { category: 'UI Essentials', name: 'icon-slashed-speaker' };
const INFO: AmenityIcon = { category: 'Semantics', name: 'icon-circle-info' };

const AMENITY_ICONS: Record<string, AmenityIcon> = {
  SIZE: { category: 'Services', name: 'icon-room-area' },
  MAX_OCCUPANCY: { category: 'People and users', name: 'icon-group' },
  BEDROOMS_QUANTITY: { category: 'Services', name: 'icon-double-bed' },
  BATH_QUANTITY: BATHTUB,
  AIR_CONDITIONED: { category: 'Services', name: 'icon-air-conditioner' },
  BATHTUB,
  DISHWASHER: { category: 'Services', name: 'icon-dishwasher' },
  INSONORIZATION: SOUNDPROOF,
  KITCHEN: { category: 'Services', name: 'icon-oven' },
  PRIVATE_BATHROOM: BATHTUB,
  SOUNDPROOFED: SOUNDPROOF,
  TV: { category: 'Services', name: 'icon-media' },
  VIEWS: { category: 'Services', name: 'icon-island' },
  WIFI: { category: 'Services', name: 'icon-wifi' },
};

const amenityIcon = (amenityType: string): AmenityIcon => AMENITY_ICONS[amenityType] ?? INFO;

export const RoomMainAmenity = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 0;
  gap: 8px;
  hyphens: auto;
  overflow-x: hidden;
  line-height: 20px;

  svg {
    height: 16px;
    width: 16px;
    flex-shrink: 0;
  }
`;

export const RoomMainAmenitiesList = styled.div`
  &:empty {
    display: none;
  }
`;

export default function RoomMainAmenities(props: RoomMainAmenitiesProps) {
  const { amenities, iconColor, iconSize, color = 'textPrimary', textVariant = 'BodySmall', ...rest } = props;

  if (!amenities) return null;

  // REV: filter(({ type }) => type !== 'BEDROOMS_QUANTITY')
  // OPX-2665 workaround to avoid showing bedrooms quantity, remove it when supply fixes the issue
  return (
    <RoomMainAmenitiesList role="list" {...rest}>
      {amenities
        ?.filter(({ type }) => type !== 'BEDROOMS_QUANTITY')
        .map(({ type, description }, i) => {
          const icon = amenityIcon(type);

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={i}>
              {icon && (
                <RoomMainAmenity role="listitem">
                  <Icon {...icon} size={iconSize} color={iconColor} />
                  <Text variant={textVariant} color={color}>
                    {description}
                  </Text>
                </RoomMainAmenity>
              )}
            </Fragment>
          );
        })}
    </RoomMainAmenitiesList>
  );
}

export function RoomMainAmenitiesInline(props: RoomMainAmenitiesInlineProps) {
  const { amenities } = props;

  if (!amenities) return null;

  // REV: filter(({ type }) => type !== 'BEDROOMS_QUANTITY')
  // OPX-2665 workaround to avoid showing bedrooms quantity, remove it when supply fixes the issue
  return (
    <TranslateSafeText>
      {amenities
        ?.filter(({ type }) => type !== 'BEDROOMS_QUANTITY')
        .map((amenity) => amenity.description)
        .join(' • ')}
    </TranslateSafeText>
  );
}

export const Separator = styled.hr`
  border: 0;
  border-top: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderDisabled};
  width: 100%;
  margin-block-start: 0;
  margin-block-end: 0;
`;
